import React, { useState, useMemo } from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text'
import Slider from 'core-system/Slider/Slider'
import FlexContainer from 'core-system/FlexContainer'
import palette from 'core-system/Themes/palette'
import Card from 'core-system/Card'
import ColorPill from 'core-system/Pill/ColorPill'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import { Locations } from 'shared/Router/Locations'
import { useNavigate } from 'react-router-dom'
import Box from '../components/Box'

const RecommenderContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${palette.white};
  border-radius: 0.9375rem;
  overflow: hidden;
  cursor: default;
`

const RecommenderHeader = styled.div`
  padding: 1.5rem 1.5rem 0.75rem 1.5rem;
`

const RecommenderContent = styled(Card)`
  flex: 1;
  padding: 1rem;
  overflow-y: auto;
  margin: 1rem;
  cursor: default;
`

const SliderSection = styled.div`
  margin-bottom: 2rem;
`

const SliderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`

const CoverageDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const CoverageValue = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const SliderContainer = styled.div`
  position: relative;
  padding: 0 0.5rem;
`

const ProgramsSection = styled.div`
  margin-bottom: 2rem;
`

const ProgramsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;
`

const MetricsCard = styled.div`
  background: ${palette.grey.grey100};
  border: 1px solid ${palette.grey.grey200};
  border-radius: 0.75rem;
  padding: 1.25rem;
  margin: 1rem 1rem 0rem 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`

const MetricRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }
`

const MetricLabel = styled.div`
  font-size: 0.875rem;
  color: ${palette.text.secondary};
  font-weight: 500;
`

const getProgramGradient = (programId: string) => {
  // Check if program is pre-tax
  const isPretax = programId.startsWith('pretax-')

  if (isPretax) {
    return `linear-gradient(135deg, ${palette.success.green50} 0%, ${palette.success.green100} 100%)`
  } else {
    return `linear-gradient(135deg, ${palette.accent.blue50} 0%, ${palette.accent.blue100} 100%)`
  }
}

const ProgramCard = styled.div<{ programId: string }>`
  background: ${(props) => getProgramGradient(props.programId)};
  border: 1px solid ${palette.grey.grey200};
  border-radius: 0.75rem;
  padding: 0.75rem;
  transition: all 0.2s ease;
  cursor: default;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: ${(props) => {
      const isPretax = props.programId.startsWith('pretax-')
      return isPretax ? palette.success.green400 : palette.accent.blue400
    }};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-1px);
  }
`

const ProgramInfo = styled.div`
  flex: 1;
`

const ProgramTitle = styled.div`
  font-weight: 600;
  font-size: 0.875rem;
  color: ${palette.text.primary};
  margin-bottom: 0.25rem;
`

const ProgramSubsidy = styled.div`
  font-size: 0.75rem;
  color: ${palette.text.secondary};
  margin-bottom: 0.5rem;
`

const ProgramStats = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const TaxTypePill = styled.div`
  //margin-bottom: 0.25rem;
`

const MaxCost = styled.div`
  font-size: 0.75rem;
  color: ${palette.text.secondary};
  text-align: right;
  font-weight: 600;
  margin-bottom: 0.35rem;
`

const BonusPerksSection = styled.div`
  background: linear-gradient(
    135deg,
    ${palette.accent.blue50} 0%,
    ${palette.accent.blue100} 100%
  );
  border: 1px solid ${palette.accent.blue200};
  border-radius: 0.9375rem;
  padding: 1rem;
  margin-bottom: 2rem;
`

const BonusCardsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1rem;
`

const BonusCard = styled.div`
  background: ${palette.white};
  border: 1px solid ${palette.accent.blue200};
  border-radius: 0.5rem;
  padding: 0.75rem;
  transition: all 0.2s ease;
  cursor: default;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: ${palette.accent.blue400};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }
`

const BonusInfo = styled.div`
  flex: 1;
`

const BonusTitle = styled.div`
  font-weight: 600;
  font-size: 0.875rem;
  color: ${palette.text.primary};
`

const BonusValue = styled.div`
  font-size: 0.75rem;
  color: ${palette.text.secondary};
  font-weight: 500;
`

interface Program {
  id: string
  name: string
  coverageThreshold: number
  subsidy?: string
  subtitle?: string
  isPreTax?: boolean
}

const getPrograms = (
  coverage: number,
  cityCosts: {
    transit: number
    micromobility: number
    car: number
  }
): Program[] => {
  const maxTransitParking = Math.max(cityCosts.transit, cityCosts.car)
  const maxRideshareTrip = 50

  if (coverage === 0) {
    return [
      {
        id: 'pretax-transit',
        name: 'Pre-tax transit',
        coverageThreshold: 0,
        subsidy: 'Employee Funded',
        isPreTax: true,
      },
      {
        id: 'pretax-parking',
        name: 'Pre-tax parking',
        subsidy: 'Employee Funded',
        coverageThreshold: 0,
        isPreTax: true,
      },
    ]
  } else if (coverage > 0 && coverage <= 50) {
    return [
      {
        id: 'pretax-transit',
        name: 'Pre-tax Transit',
        coverageThreshold: 50,
        subsidy: `$${cityCosts.transit}/mo`,
        isPreTax: true,
      },
      {
        id: 'pretax-parking',
        name: 'Pre-tax Parking',
        subsidy: 'Employee Funded',
        coverageThreshold: 50,
        isPreTax: true,
      },
    ]
  } else if (coverage > 50 && coverage <= 75) {
    return [
      {
        id: 'pretax-subsidy',
        name: 'Pre-tax Subsidy',
        coverageThreshold: 75,
        subsidy: `$${maxTransitParking}/mo`,
        subtitle: 'Transit & Parking',
        isPreTax: true,
      },
    ]
  } else if (coverage > 75 && coverage <= 90) {
    return [
      {
        id: 'pretax-subsidy',
        name: 'Pre-tax Subsidy',
        coverageThreshold: 90,
        subsidy: `$${maxTransitParking}/mo`,
        subtitle: 'Transit & Parking',
        isPreTax: true,
      },
      {
        id: 'micromobility',
        name: `Micromobility Subsidy: $${cityCosts.micromobility}/mo`,
        coverageThreshold: 90,
        subsidy: `$${cityCosts.micromobility}/mo`,
        isPreTax: false,
      },
    ]
  } else if (coverage > 90 && coverage <= 95) {
    return [
      {
        id: 'pretax-subsidy',
        name: 'Pre-tax Subsidy',
        coverageThreshold: 95,
        subsidy: `$${maxTransitParking}/mo`,
        subtitle: 'Transit & Parking',
        isPreTax: true,
      },
      {
        id: 'micromobility',
        name: 'Micromobility Subsidy',
        coverageThreshold: 95,
        subsidy: `$${cityCosts.micromobility}/mo`,
        isPreTax: false,
      },
      {
        id: 'carpool',
        name: 'Emergency Rideshare Program',
        coverageThreshold: 95,
        subsidy: `$${maxRideshareTrip * 2}/mo`,
        isPreTax: false,
      },
    ]
  } else {
    // 100%
    return [
      {
        id: 'pretax-subsidy',
        name: 'Pre-tax Subsidy',
        coverageThreshold: 100,
        subsidy: `$${maxTransitParking}/mo`,
        subtitle: 'Transit & Parking',
        isPreTax: true,
      },
      {
        id: 'micromobility',
        name: 'Micromobility Subsidy',
        coverageThreshold: 100,
        subsidy: `$${cityCosts.micromobility}/mo`,
        isPreTax: false,
      },
      {
        id: 'carpool',
        name: 'Emergency Rideshare Program',
        coverageThreshold: 100,
        subsidy: `$${maxRideshareTrip * 2}/mo`,
        isPreTax: false,
      },
      {
        id: 'commute-behavior',
        name: 'Commute Behavior Incentives',
        coverageThreshold: 100,
        subsidy: '$50/mo',
        isPreTax: false,
      },
    ]
  }
}

interface ProgramRecommenderProps {
  carReach: number
  transitReach: number
  cityCosts: {
    transit: number
    micromobility: number
    car: number
  }
  selectedWorksiteName: string
}

const ProgramRecommender: React.FC<ProgramRecommenderProps> = ({
  carReach,
  transitReach,
  cityCosts,
  selectedWorksiteName,
}) => {
  const navigate = useNavigate()
  const [coverage, setCoverage] = useState(0)

  // Add this useEffect to update coverageName when coverage changes
  const updateCoverageName = () => {
    if (coverage === 0) {
      return 'Base Compliance'
    } else if (coverage <= 50 && coverage > 0) {
      return '100% Transit Subsidy'
    } else if (coverage <= 75 && coverage > 50) {
      return '100% Transit & Parking Subsidy'
    } else if (coverage <= 90 && coverage > 75) {
      return 'Full Coverage (Transit, Parking, Micro)'
    } else if (coverage <= 95 && coverage > 90) {
      return 'Butts in Seats Safety Net'
    } else if (coverage === 100 && coverage > 95) {
      return 'Net Zero'
    }
  }

  const programs = useMemo(() => {
    return getPrograms(coverage, cityCosts)
  }, [coverage, cityCosts])

  const recommendedPrograms = useMemo(() => {
    return programs
  }, [programs])

  const maxRideshareTrip = 40

  // Calculate total employer subsidy based on the new formula
  const totalEmployerSubsidy = () => {
    if (coverage === 0) {
      return 0
    } else if (coverage > 0 && coverage <= 50) {
      return cityCosts.transit
    } else if (coverage > 50 && coverage <= 75) {
      return Math.max(cityCosts.transit, cityCosts.car)
    } else if (coverage > 75 && coverage <= 90) {
      return (
        Math.max(cityCosts.transit, cityCosts.car) + cityCosts.micromobility
      )
    } else if (coverage > 90 && coverage <= 95) {
      return (
        Math.max(cityCosts.transit, cityCosts.car) +
        cityCosts.micromobility +
        maxRideshareTrip * 2
      )
    } else if (coverage === 100) {
      return (
        Math.max(cityCosts.transit, cityCosts.car) +
        cityCosts.micromobility +
        maxRideshareTrip * 2 +
        50 // Commute Behavior Incentives
      )
    } else {
      return 0
    }
  }

  // Calculate total program reach based on the new formula
  const totalProgramReach = () => {
    if (coverage === 0) {
      return Math.max(transitReach, carReach)
    } else if (coverage > 0 && coverage <= 50) {
      return Math.max(transitReach, carReach)
    } else if (coverage > 50 && coverage <= 75) {
      return Math.max(transitReach, carReach)
    } else if (coverage > 75 && coverage <= 90) {
      return 100
    } else if (coverage > 90 && coverage <= 95) {
      return 100
    } else if (coverage === 100) {
      return 100
    } else {
      return 100
    }
  }

  // Calculate benchmark based on the new formula
  const benchmarkPercentile = () => {
    if (coverage <= 0) {
      return 0
    } else if (coverage <= 50) {
      return 50
    } else if (coverage <= 75) {
      return 75
    } else if (coverage <= 90) {
      return 90
    } else {
      // 95% and 100%
      return 95
    }
  }

  return (
    <Box>
      <RecommenderContainer>
        <RecommenderHeader>
          <FlexContainer flexDirection='row' alignItems='center' mb='0.5rem'>
            <Text
              variant='action1'
              marginRight='0.5rem'
              color={palette.text.primary}
            >
              Program Recommender:
            </Text>
            <Text
              variant='action1'
              color={palette.secondary.purple600}
              fontWeight='600'
            >
              {selectedWorksiteName}
            </Text>
          </FlexContainer>
          <Text variant='body2' color={palette.text.secondary}>
            Adjust the slider to explore program options from minimum compliance
            to a fully-loaded package.
          </Text>
        </RecommenderHeader>

        <MetricsCard>
          <MetricRow>
            <MetricLabel>Total Employer Subsidy</MetricLabel>
            <Text variant='h4' color={palette.text.primary}>
              ${totalEmployerSubsidy()}/mo
            </Text>
          </MetricRow>
          <MetricRow>
            <MetricLabel>Total Program Reach</MetricLabel>
            <Text variant='h4' color={palette.text.primary}>
              {totalProgramReach()}%
            </Text>
          </MetricRow>
          <MetricRow>
            <Text variant='action4' color={palette.text.secondary}>
              Benchmark
            </Text>
            <Text variant='action4' color={palette.text.secondary}>
              {benchmarkPercentile()}th percentile
            </Text>
          </MetricRow>
        </MetricsCard>

        <RecommenderContent>
          <SliderSection>
            <SliderHeader>
              <Text
                variant='action3'
                fontWeight='600'
                color={palette.text.primary}
              >
                Program Investment Level
              </Text>
              <CoverageDisplay>
                <CoverageValue>
                  <Text
                    variant='action4'
                    fontWeight='700'
                    color={palette.secondary.purple600}
                  >
                    {updateCoverageName()}
                  </Text>
                </CoverageValue>
              </CoverageDisplay>
            </SliderHeader>
            <SliderContainer>
              <Slider
                minLabel='Minimum Compliance'
                maxLabel='Fully Loaded'
                min={0}
                max={100}
                step={5}
                startPoint={coverage}
                onChange={(value) => setCoverage(value)}
              />
            </SliderContainer>
          </SliderSection>

          <ProgramsSection>
            <Text
              variant='action3'
              fontWeight='600'
              mb='0.75rem'
              color={palette.text.primary}
            >
              Program Breakdown ({recommendedPrograms.length})
            </Text>
            <ProgramsGrid>
              {recommendedPrograms.map((program) => (
                <ProgramCard key={program.id} programId={program.id}>
                  <ProgramInfo>
                    <ProgramTitle>{program.name}</ProgramTitle>
                    {program.subtitle && (
                      <ProgramSubsidy>{program.subtitle}</ProgramSubsidy>
                    )}
                  </ProgramInfo>
                  <ProgramStats>
                    {program.subsidy && (
                      <>
                        <MaxCost>{program.subsidy}</MaxCost>
                        <TaxTypePill>
                          <ColorPill
                            color={program.isPreTax ? 'green' : 'blue'}
                            text={program.isPreTax ? 'Pre-tax' : 'Post-tax'}
                          />
                        </TaxTypePill>
                      </>
                    )}
                  </ProgramStats>
                </ProgramCard>
              ))}
            </ProgramsGrid>
          </ProgramsSection>

          <BonusPerksSection>
            <Text
              variant='action3'
              fontWeight='600'
              mb='1rem'
              color={palette.text.primary}
            >
              🎁 Bonus Perks Included:
            </Text>
            <BonusCardsGrid>
              <BonusCard>
                <BonusInfo>
                  <BonusTitle>Gov Guaranteed Ride Home</BonusTitle>
                </BonusInfo>
                <BonusValue>localized</BonusValue>
              </BonusCard>
              <BonusCard>
                <BonusInfo>
                  <BonusTitle>Scoop Carpool</BonusTitle>
                </BonusInfo>
                <BonusValue>localized</BonusValue>
              </BonusCard>
              <BonusCard>
                <BonusInfo>
                  <BonusTitle>Bike/Scooter Corporate Discounts</BonusTitle>
                </BonusInfo>
                <BonusValue>10%</BonusValue>
              </BonusCard>
            </BonusCardsGrid>
          </BonusPerksSection>

          <PrimaryButton
            onClick={() => {
              navigate(Locations.Programs.Overview)
            }}
          >
            Launch Programs
          </PrimaryButton>
        </RecommenderContent>
      </RecommenderContainer>
    </Box>
  )
}

export default ProgramRecommender
